import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import '../../../resources/global.css';

const PortfolioAccountAccordion = ({ accounts = [] }) => {
  const [expandedAccounts, setExpandedAccounts] = useState([]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-ZA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value) || 0);
  };

  const formatDate = (date) => {
    if (!date) {
      return '';
    }
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleToggle = (accountId) => {
    setExpandedAccounts((current) => {
      if (current.includes(accountId)) {
        return current.filter((id) => id !== accountId);
      }
      return [...current, accountId];
    });
  };

  if (!Array.isArray(accounts) || accounts.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {' '}
      {accounts.map((account, accountIndex) => {
        const accountKey = account.accountId || `account-${accountIndex}`;
        const isExpanded = expandedAccounts.includes(accountKey);
        return (
          <Box
            key={accountKey}
            sx={{
              width: '100%',
              backgroundColor: isExpanded ? 'var(--ft-light-grey)' : 'var(--ft-white)',
              border: isExpanded ? 'none' : '1px solid var(--ft-mid-grey)',
              boxSizing: 'border-box',
              padding: '16px'
            }}>
            {' '}
            {/* Account Header */}{' '}
            <Box
              onClick={() => handleToggle(accountKey)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                cursor: 'pointer',
                userSelect: 'none'
              }}>
              {' '}
              {/* Account Information */}{' '}
              <Box
                sx={{
                  minWidth: 0,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                {' '}
                {/* Account Name */}{' '}
                <Typography
                  component="h5"
                  sx={{
                    fontFamily: 'var(--ft-h5-font-family)',
                    fontSize: 'var(--ft-h5-font-size)',
                    fontWeight: 'var(--ft-h5-font-weight)',
                    lineHeight: 'var(--ft-h5-line-height)',
                    color: 'var(--ft-dark)',
                    margin: 0
                  }}>
                  {' '}
                  {account.accountName}{' '}
                </Typography>{' '}
                {/* Account Number */}{' '}
                <Typography
                  sx={{
                    fontFamily: 'Raleway',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: 'var(--ft-body-small-line-height, 1.4)',

                    color: 'var(--ft-dark)',
                    margin: 0
                  }}>
                  {' '}
                  Acc: {account.accountNr}{' '}
                </Typography>{' '}
                {/* Account Amount */}{' '}
                <Typography
                  component="span"
                  sx={{
                    fontFamily: 'Raleway',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '16px',

                    color: 'var(--ft-dark)',
                    margin: 0,
                    marginTop: '4px'
                  }}>
                  {' '}
                  R {formatCurrency(account.accountNav)}{' '}
                </Typography>{' '}
              </Box>{' '}
              {/* Expand Button */}{' '}
              <IconButton
                size="small"
                aria-label={isExpanded ? 'Collapse account' : 'Expand account'}
                onClick={(event) => {
                  event.stopPropagation();
                  handleToggle(accountKey);
                }}
                sx={{
                  color: 'var(--ft-dark)',
                  padding: '4px',
                  flexShrink: 0,
                  marginTop: '0',
                  '& svg': { transition: 'transform 0.2s ease' },
                  ...(isExpanded && { '& svg': { transform: 'rotate(180deg)' } })
                }}>
                {' '}
                <ExpandMoreIcon />{' '}
              </IconButton>{' '}
            </Box>{' '}
            {/* Account Details */}
            {isExpanded && (
              <Box
                sx={{
                  marginTop: '16px'
                }}>
                {/* Full-width divider */}
                <Box
                  sx={{
                    height: '1px',
                    backgroundColor: 'var(--ft-mid-grey)',
                    marginLeft: '-16px',
                    marginRight: '-16px',
                    marginBottom: '4px'
                  }}
                />

                {Array.isArray(account.funds) &&
                  account.funds.map((fund, fundIndex) => (
                    <Box
                      key={`${account.accountId}-${fund.fundName}-${fundIndex}`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '4px',
                        padding: '12px 0'
                      }}>
                      {/* Fund Name */}
                      <Typography
                        sx={{
                          fontFamily: 'Raleway',
                          fontSize: '14px',
                          fontWeight: 400,
                          lineHeight: '16px',
                          color: 'var(--ft-dark)',
                          margin: 0
                        }}>
                        {fund.fundName}
                      </Typography>

                      {/* Fund Amount */}
                      <Typography
                        sx={{
                          fontFamily: 'Raleway',
                          fontSize: '14px',
                          fontWeight: 600,
                          lineHeight: '100%',
                          color: 'var(--ft-dark)',
                          margin: 0
                        }}>
                        R {formatCurrency(fund.nav)}
                      </Typography>

                      {/* Fund Date */}
                      {fund.priceDate && (
                        <Typography
                          sx={{
                            fontFamily: 'Raleway',
                            fontSize: '12px',
                            fontWeight: 400,
                            lineHeight: 'var(--ft-body-small-line-height, 1.4)',
                            color: 'var(--ft-dark)',
                            margin: 0
                          }}>
                          {formatDate(fund.priceDate)}
                        </Typography>
                      )}
                    </Box>
                  ))}
              </Box>
            )}{' '}
          </Box>
        );
      })}{' '}
    </Box>
  );
};
export default PortfolioAccountAccordion;
